import React from 'react';
import {
  Box,
  Card,
  CardContent,
  CardMedia,
  Typography,
  Avatar,
  Divider
} from '@mui/material';
import {
  Facebook as FacebookIcon,
  Instagram as InstagramIcon,
  Twitter as TwitterIcon,
  ThumbUp as LikeIcon,
  Comment as CommentIcon,
  Share as ShareIcon
} from '@mui/icons-material';

export default function SocialPreview({ post }) {
  const getPlatformColor = (platform) => {
    switch (platform) {
      case 'facebook': return '#1877F2';
      case 'instagram': return '#E4405F';
      case 'twitter': return '#1DA1F2';
      default: return '#666';
    }
  };

  const getPlatformIcon = (platform) => {
    switch (platform) {
      case 'facebook': return <FacebookIcon />;
      case 'instagram': return <InstagramIcon />;
      case 'twitter': return <TwitterIcon />;
      default: return null;
    }
  };

  const renderFacebookPreview = () => (
    <Card sx={{ maxWidth: 500, mx: 'auto' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Avatar sx={{ bgcolor: getPlatformColor('facebook'), mr: 2 }}>
            <FacebookIcon />
          </Avatar>
          <Box>
            <Typography variant="subtitle1" fontWeight="bold">
              PaceEstate
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Just now · Public
            </Typography>
          </Box>
        </Box>
        <Typography variant="body1" sx={{ mb: 2, whiteSpace: 'pre-wrap' }}>
          {post.content}
        </Typography>
      </CardContent>
      {post.image_url && (
        <CardMedia
          component="img"
          image={post.image_url}
          alt="Post image"
        />
      )}
      <CardContent>
        <Divider sx={{ mb: 1 }} />
        <Box sx={{ display: 'flex', justifyContent: 'space-around' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
            <LikeIcon fontSize="small" /> Like
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
            <CommentIcon fontSize="small" /> Comment
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
            <ShareIcon fontSize="small" /> Share
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  const renderInstagramPreview = () => (
    <Card sx={{ maxWidth: 500, mx: 'auto' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Avatar sx={{ bgcolor: getPlatformColor('instagram'), mr: 2 }}>
            <InstagramIcon />
          </Avatar>
          <Typography variant="subtitle1" fontWeight="bold">
            paceestate
          </Typography>
        </Box>
      </CardContent>
      {post.image_url && (
        <CardMedia
          component="img"
          image={post.image_url}
          alt="Post image"
          sx={{ aspectRatio: '1/1', objectFit: 'cover' }}
        />
      )}
      <CardContent>
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
          <strong>paceestate</strong> {post.content}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          Just now
        </Typography>
      </CardContent>
    </Card>
  );

  const renderTwitterPreview = () => (
    <Card sx={{ maxWidth: 500, mx: 'auto' }}>
      <CardContent>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Avatar sx={{ bgcolor: getPlatformColor('twitter') }}>
            <TwitterIcon />
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Typography variant="subtitle1" fontWeight="bold">
                PaceEstate
              </Typography>
              <Typography variant="body2" color="text.secondary">
                @paceestate · now
              </Typography>
            </Box>
            <Typography variant="body1" sx={{ mb: 2, whiteSpace: 'pre-wrap' }}>
              {post.content}
            </Typography>
            {post.image_url && (
              <Box
                component="img"
                src={post.image_url}
                alt="Post image"
                sx={{
                  width: '100%',
                  borderRadius: 2,
                  mb: 1
                }}
              />
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ py: 2 }}>
      <Typography variant="h6" gutterBottom sx={{ textAlign: 'center', mb: 3 }}>
        {post.platform.charAt(0).toUpperCase() + post.platform.slice(1)} Preview
      </Typography>
      {post.platform === 'facebook' && renderFacebookPreview()}
      {post.platform === 'instagram' && renderInstagramPreview()}
      {post.platform === 'twitter' && renderTwitterPreview()}
    </Box>
  );
}
